import {graphql} from 'gatsby';
import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';

import MapArtist from '../components/map-artist';
import PageElementGroup from '../components/page-element-group';
import * as iter from '../utils/iter';

import style from './map.module.scss';

/**
 * @typedef {(val: string | null | ((prev: string | null) => string | null)) => void} SetOpenArtist
 */

const Artist = memo(
	/**
	 * @param {{
	 *   openArtist: null | string,
	 *   setOpenArtist: SetOpenArtist,
	 *   selectMapTarget: (slug: string) => void,
	 *   artist: Pick<import("../../tmp/graphql-types").ArtistsJson, "id" | "slug"> &
	 *     import("../../tmp/graphql-types").MapArtistFragment,
	 * }} props
	 * @returns {React.ReactElement} el
	 */
	function Artist({artist, openArtist, setOpenArtist, selectMapTarget}) {
		const toggleOpen = useCallback(
			() => {
				setOpenArtist((prev) => {
					if (prev === artist.slug) {
						return null;
					} else {
						return artist.slug;
					}
				});
			},
			[setOpenArtist, artist.slug],
		);

		const selectMapTarget2 = useCallback(
			() => {
				selectMapTarget(artist.slug);
			},
			[selectMapTarget, artist.slug],
		);

		return (
			<MapArtist
				open={openArtist === artist.slug}
				toggleOpen={toggleOpen}
				artist={artist}
				selectMapTarget={selectMapTarget2}
			/>
		);
	}
);

export const query = graphql`
	query MapPage {
		allArtistsJson(sort: {order: ASC, fields: order}) {
			nodes {
				id
				slug
				fixedProject
				...MapArtist
			}
		}

		allPagesJson(filter: {slug: {eq: "map"}}) {
			nodes {
				...PageElementGroup
			}
		}
	}
`;

export default memo(
	/**
	 * @param {import("gatsby").PageProps<
	 *   import("../../tmp/graphql-types").MapPageQuery,
	 *   {
	 *     hashState: number,
	 *     meta: import("../page-metadata").PageMetadata,
	 *   }
	 * >} props
	 */
	function MapPage({data, location, pageContext: {hashState, meta}}) {
		// no way to define generic types in jsdocs
		const openArtist_ = useState(null);
		/** @type {[string | null, React.Dispatch<React.SetStateAction<string | null>>]} */
		const [openArtist, setOpenArtist] = openArtist_;

		// open hash-linked artist on page load
		useEffect(
			() => {
				if (typeof location.hash === 'string') {
					setOpenArtist(location.hash.substring(1));
				}
			},
			[location.key],
		);

		const intl = useIntl();
		const mapEmbedSrc = intl.locale === 'en' ?
			'/lichtparcours2020/map-embed-en.php?braunschweig-iframe-embed' :
			'/lichtparcours2020/map-embed.php?braunschweig-iframe-embed';

		/** @type {React.MutableRefObject<HTMLIFrameElement | null>} */
		const mapIframeRef = useRef(null);

		const selectMapTarget = useCallback(
			/**
			 * @param {string} slug
			 */
			(slug) => {
				if (mapIframeRef.current && mapIframeRef.current.contentWindow) {
					mapIframeRef.current.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
						inline: 'nearest',
					});
					mapIframeRef.current.contentWindow.postMessage(
						{type: 'selectTarget', slug},
						'*',
					);
				}
			},
			[],
		);

		return (
			<div className={style.base}>
				<iframe
					className={style.mapIframe}
					sandbox="allow-same-origin allow-scripts allow-top-navigation"
					allow="fullscreen geolocation gyroscope ambient-light-sensor"
					src={mapEmbedSrc}
					ref={mapIframeRef}
				/>

				<PageElementGroup
					name="start"
					nodes={data.allPagesJson.nodes}
					meta={meta}
					hashState={hashState}
				/>

				<section className={style.artists}>
					{iter.into(data.allArtistsJson.nodes)
						.filter((a) => !a.fixedProject)
						.map((a) => (
							<Artist
								key={a.id}
								artist={a}
								setOpenArtist={setOpenArtist}
								openArtist={openArtist}
								selectMapTarget={selectMapTarget}
							/>
						))
						.toArray()
					}
				</section>

				<PageElementGroup
					name="middle"
					nodes={data.allPagesJson.nodes}
					meta={meta}
					hashState={hashState}
				/>

				<section className={style.artists}>
					{iter.into(data.allArtistsJson.nodes)
						.filter((a) => a.fixedProject)
						.map((a) => (
							<Artist
								key={a.id}
								artist={a}
								setOpenArtist={setOpenArtist}
								openArtist={openArtist}
								selectMapTarget={selectMapTarget}
							/>
						))
						.toArray()
					}
				</section>
			</div>
		);
	}
);
